import * as Sentry from '@sentry/nextjs';
import axios from 'axios';
import Cookies from 'universal-cookie';
import { z } from 'zod';

import { getUniversalCookie, removeUniversalCookie } from '@/cookies';
import { shouldDropErrorEvent } from '@/lib/errorFilter';
import { generateUUID, log } from '@/lib/utils';
import { ASSET_TYPE } from '@/types/asset';
import { getConversion } from '@/utils/analytics/conversion';

import type { GetServerSidePropsContext, NextApiResponse } from 'next';

// This should be replaced by .NET API.

const eventType = [
  'download',
  'play',
  'download_toggle_platform',
  'page_view',
  'search',
  'purchase',
  'login',
  'register',
  'aipg_generate',
  'logout',
  'favourite',
  'add_to_board',
  'create_board',
  'delete_board',
  'rename_board',
  'safelist_channel',
  'remove_safelist_channel',
  'safelist_fetch_failed',
  'safelist_prompt_viewed',
  'enable_auto_renew',
  'disable_auto_renew',
  'pause_subscription',
  'resume_subscription',
  'downgrade_subscription',
  'how_did_you_hear_about_us',
  'click',
  'experiment_viewed',
  'contact_form_submitted',
  'modal_screen_view',
  'error',
  'sentry',
  'referral_link_generated',
  'keyboard_shortcut_triggered',
  'clip_sync',
  'web_audio_graph_created',
  'audio_context_state_change',
  'audio_context_resume_failed',
  'play_started_silently_suspected',
  'playback_threshold_reached',
  'adaptive_music_feedback',
  'adaptive_music_duration_demand',
  'stems_interaction',
  // Sign-up plan-step experiment (DEV-7091)
  'signup_plan_step_viewed',
  'signup_plan_step_plan_selected',
  'signup_plan_step_continue_free',
  'signup_plan_step_explore_plans',
  'signup_plan_step_closed',
] as const;

export const metadataSchema = z.object({
  asset_type: z
    .enum([
      ASSET_TYPE.MUSIC,
      ASSET_TYPE.SFX,
      ASSET_TYPE.MOTION,
      ASSET_TYPE.STOCK_VIDEO,
    ])
    .optional()
    .describe('Asset type'),
  query_string: z.record(z.string()).optional().describe('Query string'),
  type: z.string().optional().describe('Type'),
  text: z.string().optional().describe('Text'),
  page_type: z
    .string()
    .optional()
    .describe('Page context (getPageType contentType), e.g. Show-free-first'),
  code: z.string().optional().describe('Code'),
  keys: z.array(z.string()).optional().describe('Keyboard keys'),
  channel_id: z.string().optional().describe('Channel ID'),
  asset_id: z.number().optional().describe('Asset ID'),
  platform: z.string().optional().describe('Platform'),
  payment_type: z.string().optional().describe('Payment type'),
  plan_type: z.string().optional().describe('Plan type'),
  target_plan: z.string().optional().describe('Plan being moved to'),
  plan_length: z.string().optional().describe('Length'),
  pause_duration_months: z
    .number()
    .optional()
    .describe('Pause duration test group (1/2/3 months)'),
  currency: z.string().optional().describe('Currency'),
  value: z.number().optional().describe('Value'),
  variant_id: z.number().optional().describe('Variant ID'),
  next_path: z.string().optional().describe('Next path'),
  conversion_source: z.string().optional().describe('Conversion source'),
  conversion_path: z.string().optional().describe('Conversion origin path'),
  conversion_asset_type: z
    .string()
    .optional()
    .describe('Conversion asset type'),
  conversion_asset_id: z.number().optional().describe('Conversion asset ID'),
  experiment_id: z.string().optional().describe('Experiment ID'),
  signup_method: z
    .enum(['google', 'email'])
    .optional()
    .describe('How the account was created'),
  plan_step_flow: z
    .enum([
      'general',
      'download-plan-step',
      'download-plan-choice',
      'download-premium-visitor',
    ])
    .optional()
    .describe('Journey the sign-up plan step was shown in'),
  variation_id: z.number().optional().describe('Variation ID'),
  device_id: z.string().optional().describe('Device ID'),
  subject: z.array(z.string()).optional().describe('Subject'),
  format: z.string().optional().describe('File format'),
  screen_name: z.string().optional().describe('Screen name'),
  popup_id: z.string().optional().describe('Popup ID'),
  popup_variant: z
    .string()
    .optional()
    .describe('Pop-up experiment variant shown, e.g. A, B or C'),
  partner_key: z
    .string()
    .optional()
    .describe('PartnerStack partner key the visitor was referred by'),
  creator_name: z.string().optional().describe('Name of the referring creator'),
  has_discount: z
    .boolean()
    .optional()
    .describe('Whether the referral offers a discount'),
  referral_code: z.string().optional().describe('Referral code'),
  board_id: z.number().optional().describe('Board ID'),
  board_name: z.string().optional().describe('Board name'),
  old_board_name: z
    .string()
    .optional()
    .describe('Old board name (for renames)'),
  new_board_name: z
    .string()
    .optional()
    .describe('New board name (for renames)'),
  is_favorites_board: z.boolean().optional().describe('Is favorites board'),
  is_shared_board: z.boolean().optional().describe('Is shared board'),
  board_visibility: z
    .enum(['public', 'private'])
    .optional()
    .describe('Board visibility - public/private'),
  is_authenticated: z.boolean().optional().describe('Is user authenticated'),
  from: z.string().optional().describe('Previous AudioContext state'),
  to: z.string().optional().describe('New AudioContext state'),
  is_playing: z.boolean().optional().describe('Whether playback is active'),
  browser: z.string().optional().describe('Browser label'),
  flag: z.boolean().optional().describe('Feature flag enabled'),
  context_state_initial: z
    .string()
    .optional()
    .describe('Initial AudioContext state on creation'),
  error: z.string().optional().describe('Error message'),
  track_id: z.number().optional().describe('Track identifier'),
  audio_engine: z
    .enum(['web_audio', 'html5'])
    .optional()
    .describe('Which audio playback engine served this play'),
  is_adaptive: z
    .boolean()
    .optional()
    .describe('Whether the downloaded asset is an adaptive (edited) version'),
  average_score: z
    .number()
    .optional()
    .describe('Average score of a played track'),
  source_duration: z.number().optional().describe('Duration of original asset'),
  target_duration: z
    .number()
    .optional()
    .describe('Target duration of the adapted asset'),
  adapted_results: z
    .array(
      z.object({
        edit_duration: z
          .number()
          .optional()
          .describe('Duration of adapted asset'),
        progress: z
          .number()
          .optional()
          .describe('Percentage of tracked played'),
        score: z.number().optional().describe('Score of adapted asset'),
        version: z
          .string()
          .optional()
          .describe('Version identifier of the adapted plan'),
      })
    )
    .optional()
    .describe('Adapted Music edited results'),
  edit_duration: z.number().optional().describe('Duration of adapted asset'),
  score: z.number().optional().describe('Score of adapted asset'),
  version: z
    .string()
    .optional()
    .describe('Version identifier of the downloaded adapted plan'),
  beat_confidence: z
    .number()
    .optional()
    .describe('Score of the Adapted Track beat confidence'),
  attempted_seconds: z
    .number()
    .optional()
    .describe('Raw attempted duration in seconds, before any clamp/snap'),
  allowed: z
    .boolean()
    .optional()
    .describe('Whether the attempted duration was within the supported range'),
  input_method: z
    .enum(['preset', 'custom_text', 'custom_drawer', 'video_sync'])
    .optional()
    .describe('How the duration was entered'),
  search_id: z
    .string()
    .optional()
    .describe(
      'Stable per-search dedup id: same device + query text within a 5-minute window shares one id'
    ),
  search_type: z
    .string()
    .optional()
    .describe(
      'Search tab the results relate to: music, sfx, motion_graphics, luts, stock_video'
    ),
  results_found: z
    .number()
    .optional()
    .describe('Result count for the searched asset type'),
  results_by_type: z
    .record(z.number())
    .optional()
    .describe('Result counts per asset type for the same search query'),
  blocked: z
    .boolean()
    .optional()
    .describe('Whether the search was blocked by the profanity filter'),
  // Namespaced rather than spread across the top level: the stems mixer needs a
  // dozen fields of its own and it is an experiment, so it should be removable
  // in one piece.
  stems: z
    .object({
      action: z
        .string()
        .describe('What was done, e.g. solo, point_add, envelope_mode'),
      input: z
        .enum(['click', 'keyboard', 'drag', 'menu'])
        .optional()
        .describe('How it was done'),
      track: z.string().optional().describe('Stems seed id'),
      genre: z.string().optional().describe('Genre of the track'),
      stem: z
        .string()
        .optional()
        .describe('Stem the action applied to, or "master"'),
      value: z.number().optional().describe('Value after the change'),
      previous: z.number().optional().describe('Value before the change'),
      exclusive: z
        .boolean()
        .optional()
        .describe('Solo/mute that cleared the others'),
      curve: z.string().optional().describe('Easing applied to a run'),
      points: z
        .number()
        .optional()
        .describe('Points in the lane after the change'),
      selected: z
        .number()
        .optional()
        .describe('How many points the action applied to'),
      soloed: z
        .number()
        .optional()
        .describe('Stems soloed when the action happened, before it applied'),
      muted: z
        .number()
        .optional()
        .describe('Stems muted when the action happened, before it applied'),
      lanes_shaped: z
        .number()
        .optional()
        .describe('Lanes away from their defaults when the action happened'),
      editing: z
        .boolean()
        .optional()
        .describe('Whether the envelopes were on when the action happened'),
      position: z
        .number()
        .optional()
        .describe('Playhead in seconds when the action happened'),
      time: z
        .number()
        .optional()
        .describe('Where on the track an automation point sits, in seconds'),
      target: z
        .string()
        .optional()
        .describe(
          'What was acted on when it is not a stem: line, point, dialog'
        ),
      duration: z.number().optional().describe('Track length in seconds'),
      label: z.string().optional().describe('Undo label, for undo/redo'),
    })
    .optional()
    .describe('Stems prototype interaction detail'),
});

export const eventSchema = z.object({
  event_id: z.string().describe('Unique event identifier'),
  user_ip: z.string().describe('User IP address'),
  timestamp: z.string().datetime().describe('Event timestamp in ISO format'),
  event_type: z.enum(eventType).describe('Type of event'),
  asPath: z.string().describe('Source page path'),
  session_id: z.string().describe('Session identifier'),
  metadata: metadataSchema.optional().describe('Metadata'),
  language: z.string().optional().describe('Language'),
  user_id: z.number().optional().describe('User identifier'),
  account_type: z.string().optional().describe('Account type'),
  device_type: z.string().optional().describe('Device type'),
  resolution: z
    .union([
      // Accept object format (new)
      z.object({
        width: z.number().describe('Screen width'),
        height: z.number().describe('Screen height'),
      }),
      // Accept array format (legacy) and transform it
      z.tuple([z.number(), z.number()]).transform((val) => ({
        width: val[0],
        height: val[1],
      })),
    ])
    .optional()
    .describe('Screen resolution'),
  referrer: z.string().optional().describe('Referrer URL'),
  ip_address: z.string().optional().describe('IP address'),
  country: z.string().optional().describe('Country'),
  is_bot: z.boolean().optional().describe('Is bot'),
  browser: z.string().optional().describe('Browser'),
  os: z.string().optional().describe('Operating system'),
  environment: z.string().optional().describe('Environment'),
  utm_source: z.string().optional().describe('UTM source'),
  utm_medium: z.string().optional().describe('UTM medium'),
  utm_campaign: z.string().optional().describe('UTM campaign'),
  utm_term: z.string().optional().describe('UTM term'),
  utm_content: z.string().optional().describe('UTM content'),
});

const clientEventSchema = eventSchema.omit({
  event_id: true,
  user_ip: true,
});

// Add these variables at the top level
const EVENT_BATCH: z.infer<typeof clientEventSchema>[] = [];
let batchTimeout: NodeJS.Timeout | null = null;
const BATCH_DELAY = 5000; // 5 seconds
const BATCH_SIZE_LIMIT = 10; // Maximum number of events before triggering a batch process
const EVENT_COOKIE_NAME = 'uppbeat_pending_events'; // Changed to plural to reflect multiple events

// Retry configuration
const MAX_RETRY_ATTEMPTS = 3;
const RETRY_DELAY_BASE = 1000; // 1 second base delay
const MAX_RETRY_DELAY = 10_000; // 10 seconds maximum delay

// Critical engagement events that should be stored in localStorage on failure
const CRITICAL_ENGAGEMENT_EVENTS = [
  'download',
  'play',
  'purchase',
  'favourite',
  'add_to_board',
] as const;

// LocalStorage key for failed critical events
const FAILED_EVENTS_STORAGE_KEY = 'uppbeat_failed_critical_events';
const MAX_STORED_EVENTS = 50; // Limit stored events to prevent localStorage overflow

/**
 * Checks if an event is a duplicate of any existing event within a time window.
 * @param newEvent - The new event to check
 * @param existingEvents - Array of existing events to compare against
 * @param timeWindow - Time window in milliseconds to consider events as duplicates (default: 1000ms)
 * @returns true if the event is a duplicate, false otherwise
 */
const isEventDuplicate = (
  newEvent: {
    event_type: string;
    asPath: string;
    metadata?: unknown;
    timestamp: string;
  },
  existingEvents: Array<{
    event_type: string;
    asPath: string;
    metadata?: unknown;
    timestamp: string;
  }>,
  timeWindow = 1000
): boolean =>
  existingEvents.some((existing) => {
    const timeDiff = Math.abs(
      new Date(existing.timestamp).getTime() -
        new Date(newEvent.timestamp).getTime()
    );
    return (
      existing.event_type === newEvent.event_type &&
      existing.asPath === newEvent.asPath &&
      JSON.stringify(existing.metadata) === JSON.stringify(newEvent.metadata) &&
      timeDiff < timeWindow
    );
  });

// Early-return shape on purpose: with the `typeof window` guard as an
// always-true `if` wrapping a reassigned `let`, Next's SWC minifier inlines
// the call as `undefined` in the App Router bundle, so every blog event was
// posted without a session_id and rejected by the API (DEV-6833).
export const getSessionId = () => {
  if (typeof window === 'undefined') {
    return generateUUID();
  }

  const stored = sessionStorage.getItem('session_id');

  if (stored) {
    return stored;
  }

  const session_id = generateUUID();
  sessionStorage.setItem('session_id', session_id);

  return session_id;
};

/**
 * Store critical events in localStorage for later retry
 */
const storeCriticalEventsInLocalStorage = (
  events: z.infer<typeof clientEventSchema>[]
) => {
  try {
    if (typeof window === 'undefined' || !window.localStorage) {
      return;
    }

    // Filter only critical engagement events
    const criticalEvents = events.filter((event) =>
      (CRITICAL_ENGAGEMENT_EVENTS as readonly string[]).includes(
        event.event_type
      )
    );

    if (criticalEvents.length === 0) {
      return;
    }

    // Get existing stored events
    const existingEventsJson = localStorage.getItem(FAILED_EVENTS_STORAGE_KEY);
    const existingEvents = existingEventsJson
      ? JSON.parse(existingEventsJson)
      : [];

    // Combine and limit the total stored events
    const combinedEvents = [...existingEvents, ...criticalEvents].slice(
      -MAX_STORED_EVENTS
    );

    // Store back to localStorage
    localStorage.setItem(
      FAILED_EVENTS_STORAGE_KEY,
      JSON.stringify(combinedEvents)
    );

    log(
      `Stored ${criticalEvents.length} critical events in localStorage for retry`
    );
  } catch (error) {
    // Silently fail if localStorage is not available or quota exceeded
    console.warn('Failed to store critical events in localStorage:', error);
  }
};

/**
 * Retrieve and clear stored critical events from localStorage
 */
const retrieveStoredCriticalEvents = (): z.infer<
  typeof clientEventSchema
>[] => {
  try {
    if (typeof window === 'undefined' || !window.localStorage) {
      return [];
    }

    const storedEventsJson = localStorage.getItem(FAILED_EVENTS_STORAGE_KEY);
    if (!storedEventsJson) {
      return [];
    }

    const events = JSON.parse(storedEventsJson);

    // Clear the stored events
    localStorage.removeItem(FAILED_EVENTS_STORAGE_KEY);

    log(`Retrieved ${events.length} stored critical events for retry`);
    return events;
  } catch (error) {
    console.warn('Failed to retrieve stored critical events:', error);
    return [];
  }
};

/**
 * Retry function with exponential backoff
 * @param fn - The function to retry
 * @param attempt - Current attempt number (starts at 1)
 * @returns Promise that resolves with the function result or rejects after max attempts
 */
const retryWithBackoff = async <T>(
  fn: () => Promise<T>,
  attempt = 1
): Promise<T> => {
  try {
    return await fn();
  } catch (error) {
    if (attempt >= MAX_RETRY_ATTEMPTS) {
      throw error;
    }

    // Calculate delay with exponential backoff and jitter
    const baseDelay = RETRY_DELAY_BASE * 2 ** (attempt - 1);
    const jitter = Math.random() * 0.1 * baseDelay; // Add up to 10% jitter
    const delay = Math.min(baseDelay + jitter, MAX_RETRY_DELAY);

    log(`Retry attempt ${attempt} failed, retrying in ${Math.round(delay)}ms`);

    // Wait before retry
    await new Promise((resolve) => setTimeout(resolve, delay));

    // Recursive retry
    return retryWithBackoff(fn, attempt + 1);
  }
};

/**
 * Attempt to resend stored critical events from localStorage
 * This should be called on page load or when network connectivity is restored
 */
const recoverStoredEvents = async () => {
  try {
    const storedEvents = retrieveStoredCriticalEvents();

    if (storedEvents.length === 0) {
      return;
    }

    log(`Attempting to recover ${storedEvents.length} stored critical events`);

    // Add stored events to the current batch
    storedEvents.forEach((event) => {
      // Check for duplicates before adding
      if (!isEventDuplicate(event, EVENT_BATCH)) {
        EVENT_BATCH.push(event);
      }
    });

    // If we have events and no batch is scheduled, process immediately
    if (EVENT_BATCH.length > 0 && !batchTimeout) {
      processBatch();
    }
  } catch (error) {
    console.error('Failed to recover stored events:', error);
  }
};

export const CLIENT_EVENT_BATCH_FAILED = 'client_event_batch_failed';

/**
 * True for the Sentry event `processBatch` raises when a batch POST fails.
 * `beforeSend` must not log these back as `error` events: the new event would
 * go out in the next batch, fail the same way (e.g. 429 from /api/v1/event)
 * and be reported again, looping every ~8s for as long as the tab is open
 * (UPPBEAT-5ZM).
 */
export const isClientEventBatchFailure = (event: {
  tags?: Record<string, unknown>;
}) => event.tags?.error_type === CLIENT_EVENT_BATCH_FAILED;

const processBatch = async () => {
  if (EVENT_BATCH.length > 0) {
    const batchSize = EVENT_BATCH.length;
    const eventTypes = EVENT_BATCH.map((e) => e.event_type);

    // Take the events out of the batch *before* awaiting: anything logged
    // while the POST is in flight must land in the next batch, not be wiped
    // when this one clears (lost ~20% of purchases, DEV-6834).
    const batchToSend = EVENT_BATCH.splice(0);

    try {
      // Use retry logic with exponential backoff
      await retryWithBackoff(async () =>
        axios.post('/api/v1/event', { events: batchToSend })
      );

      log(`Successfully sent batch of ${batchSize} events:`, eventTypes);
    } catch (error) {
      console.error(
        `Failed to send event batch after ${MAX_RETRY_ATTEMPTS} attempts:`,
        error
      );

      // Report batch failures to Sentry for better visibility
      Sentry.captureException(error, {
        extra: {
          batchSize,
          eventTypes,
          events: batchToSend.slice(0, 3), // Only send first 3 events to avoid large payloads
          maxRetryAttempts: MAX_RETRY_ATTEMPTS,
        },
        tags: {
          error_type: CLIENT_EVENT_BATCH_FAILED,
          batch_size: batchSize.toString(),
          retry_attempts: MAX_RETRY_ATTEMPTS.toString(),
        },
        level: 'error',
      });

      // Store critical events in localStorage so they can be retried later
      storeCriticalEventsInLocalStorage(batchToSend);

      // Log warning about events
      const criticalEventCount = batchToSend.filter((e) =>
        (CRITICAL_ENGAGEMENT_EVENTS as readonly string[]).includes(e.event_type)
      ).length;

      if (criticalEventCount > 0) {
        console.warn(
          `${criticalEventCount} critical engagement events stored in localStorage for retry`
        );
      }

      const nonCriticalCount = batchSize - criticalEventCount;
      if (nonCriticalCount > 0) {
        console.warn(
          `${nonCriticalCount} non-critical analytics events were lost due to network failure`
        );
      }
    }
  }

  batchTimeout = null;
};

/**
 * Sends the pending batch as the page is hidden or unloaded. The batch timer
 * never fires after the tab closes, so events logged in the last 5s were lost —
 * which skewed exposure counts for arms people bounce from (DEV-7278).
 */
export const flushEventBatchOnHide = () => {
  if (EVENT_BATCH.length === 0) {
    return;
  }
  if (batchTimeout) {
    clearTimeout(batchTimeout);
    batchTimeout = null;
  }

  const body = JSON.stringify({ events: EVENT_BATCH.splice(0) });
  const queued = navigator.sendBeacon?.(
    '/api/v1/event',
    new Blob([body], { type: 'application/json' })
  );
  if (!queued) {
    fetch('/api/v1/event', {
      method: 'POST',
      body,
      headers: { 'Content-Type': 'application/json' },
      keepalive: true,
    }).catch(() => {
      // Best effort: the page is going away.
    });
  }
};

export const logEvent = async (
  body: Pick<z.infer<typeof eventSchema>, 'event_type' | 'asPath' | 'metadata'>,
  processInstantly = false
) => {
  if (typeof window === 'undefined') {
    return;
  }

  // Drop non-actionable error noise before logging (DEV-5936). Mirrors the
  // Sentry beforeSend filter so directly-logged errors (e.g. video/referral
  // tracking) and the Sentry pipeline stay consistent.
  if (
    body.event_type === 'error' &&
    shouldDropErrorEvent({
      message:
        typeof body.metadata?.text === 'string' ? body.metadata.text : '',
    })
  ) {
    return;
  }

  const session_id = getSessionId();
  const timestamp = new Date().toISOString();
  const referrer = document?.referrer ?? 'direct';
  const language = navigator?.language;
  const screenWidth = window.innerWidth;
  const screenHeight = window.innerHeight;

  let device_type = 'desktop';

  if (screenWidth <= 768) {
    device_type = 'mobile';
  } else if (screenWidth <= 1024) {
    device_type = 'tablet';
  }

  // Optimize URL parsing - split once and destructure
  const [parsedAsPath, ...queryParts] = body.asPath.split('?');
  const queryString = queryParts.join('?');
  let { metadata } = body;

  // Attribute sign-ups to the asset/CTA that opened the flow, as purchase does.
  if (body.event_type === 'register') {
    metadata = { ...getConversion(), ...metadata };
  }

  // Parse and validate query string if present
  if (queryString) {
    try {
      const params = new URLSearchParams(queryString);
      const queryStringObject = Object.fromEntries(params.entries());

      // Only add query_string to metadata if it has entries and passes validation
      if (Object.keys(queryStringObject).length > 0) {
        // Validate the query string object structure
        const validatedQueryString = z
          .record(z.string())
          .safeParse(queryStringObject);

        if (validatedQueryString.success) {
          metadata = {
            ...metadata,
            query_string: validatedQueryString.data,
          };
        } else {
          console.warn('Invalid query string format:', queryStringObject);
        }
      }
    } catch (error) {
      console.warn('Failed to parse query string:', queryString, error);
    }
  }

  // Validate the final metadata before using it in the event
  const metadataResult = metadataSchema.safeParse(metadata);
  if (metadataResult.success) {
    metadata = metadataResult.data;
  } else {
    console.warn('Invalid metadata generated:', metadataResult.error);
    // Use original metadata without query_string
    metadata = body.metadata;
  }

  // Create the event object
  const event = {
    event_type: body.event_type,
    asPath: parsedAsPath,
    session_id,
    timestamp,
    metadata,
    language,
    device_type,
    resolution: { width: screenWidth, height: screenHeight },
    referrer,
  };

  // Check if this event is a duplicate in the current batch
  // Only consider it a duplicate if it's truly identical and happens very quickly (likely a double-click)
  const isDuplicate = isEventDuplicate(event, EVENT_BATCH);

  // Only add the event if it's not a duplicate
  if (isDuplicate) {
    log('Skipping duplicate event:', event.event_type);
  } else {
    // Add event to batch
    EVENT_BATCH.push(event);

    // Process batch if size limit is reached
    if (EVENT_BATCH.length >= BATCH_SIZE_LIMIT) {
      if (batchTimeout) {
        clearTimeout(batchTimeout);
      }

      await processBatch();

      return;
    }

    // Clear existing timeout if it exists
    if (batchTimeout) {
      clearTimeout(batchTimeout);
    }

    // Set new timeout
    batchTimeout = setTimeout(processBatch, BATCH_DELAY);
  }

  /**
   * Process batch instantly if processInstantly is true and there
   * are events in the batch
   */
  if (processInstantly && EVENT_BATCH.length > 0) {
    if (batchTimeout) {
      clearTimeout(batchTimeout);
    }

    await processBatch();
  }
};

/**
 * Processes any pending events stored in cookies.
 *
 * This function should be called on the client side when the application loads.
 * It will check for any events stored in cookies, log them, and then remove the cookies.
 */
export const processStoredEvents = () => {
  log('Processing stored events');

  if (typeof window === 'undefined') {
    return; // Only run on client side
  }

  try {
    // Get the stored events from the cookie
    const serializedEvents =
      getUniversalCookie<Record<string, unknown>[]>(EVENT_COOKIE_NAME);

    if (serializedEvents) {
      // Parse the event data
      let events = [];

      try {
        events = serializedEvents;

        if (!Array.isArray(events)) {
          events = [events]; // Handle case where it's a single event
        }
      } catch (parseError) {
        console.error('Failed to parse events from cookie:', parseError);
        removeUniversalCookie(EVENT_COOKIE_NAME);

        return;
      }

      // Process each event
      for (const eventData of events) {
        try {
          // Extract fields from event data
          const { event_type, asPath, metadata, timestamp } = eventData;

          // Validate event_type against schema
          const eventTypeResult = z.enum(eventType).safeParse(event_type);

          if (!eventTypeResult.success) {
            console.error('Invalid event_type:', event_type);
            continue;
          }

          // Validate asPath
          if (typeof asPath !== 'string' || !asPath) {
            console.error('Invalid asPath:', asPath);
            continue;
          }

          // Validate metadata if present
          let validatedMetadata: z.infer<typeof metadataSchema> | undefined;

          if (metadata) {
            const metadataResult = metadataSchema.safeParse(metadata);

            if (metadataResult.success) {
              validatedMetadata = metadataResult.data;
            } else {
              console.warn(
                'Invalid metadata, proceeding without it:',
                metadata
              );
            }
          }

          // Get the timestamp of the stored event, with proper validation
          let storedEventTimestamp: number;
          if (typeof timestamp === 'string') {
            const parsedTime = new Date(timestamp).getTime();
            // Check if the parsed time is valid (not NaN)
            storedEventTimestamp = Number.isNaN(parsedTime)
              ? Date.now()
              : parsedTime;
          } else {
            // If no timestamp, use current time
            storedEventTimestamp = Date.now();
          }

          // Check if this event is a duplicate in the current batch
          const isDuplicate = isEventDuplicate(
            {
              event_type: eventTypeResult.data,
              asPath,
              metadata: validatedMetadata,
              timestamp: new Date(storedEventTimestamp).toISOString(),
            },
            EVENT_BATCH
          );

          // Only log the event if it's not a duplicate
          if (isDuplicate) {
            log('Skipping duplicate stored event:', eventTypeResult.data);
          } else {
            // Log the event with validated data
            void logEvent({
              event_type: eventTypeResult.data,
              asPath,
              metadata: validatedMetadata,
            });
          }
        } catch (eventError) {
          console.error('Failed to process individual event:', eventError);
        }
      }

      // Remove the cookie after processing all events
      removeUniversalCookie(EVENT_COOKIE_NAME);
    }
  } catch (error) {
    console.error('Failed to process stored events:', error);
    // Remove the cookie even if processing fails to prevent repeated errors
    removeUniversalCookie(EVENT_COOKIE_NAME);
  }
};

/**
 * Stores an event as a cookie for server-side props context.
 *
 * This version is specifically designed to work within getServerSideProps
 * where we have direct access to the response object.
 *
 * @param res - The Next.js response object from getServerSideProps
 * @param eventData - The event data to store in the cookie
 */
export const logEventSSR = (
  res: NextApiResponse | GetServerSidePropsContext['res'],
  eventData: Omit<
    z.infer<typeof eventSchema>,
    'event_id' | 'user_ip' | 'session_id'
  > & {
    event_id?: string;
    session_id?: string;
  },
  clearEvents = false
) => {
  try {
    // Get existing events from cookie or initialize empty array
    let events: Array<typeof eventData> = [];

    // If clearEvents is true, we'll skip loading existing events
    if (!clearEvents) {
      // Check for existing cookie in the request headers
      let existingData;

      if (res.req?.headers?.cookie) {
        const cookies = new Cookies(res.req.headers.cookie);

        existingData = cookies.get(EVENT_COOKIE_NAME);
      }

      if (existingData) {
        try {
          events = existingData;

          if (!Array.isArray(events)) {
            events = [events]; // Convert single event to array if needed
          }
        } catch (parseError) {
          console.error(
            'Failed to parse existing events cookie in SSR:',
            parseError
          );
          events = [];
        }
      }
    }

    // Add the new event with required fields if missing
    const completeEvent = {
      ...eventData,
      event_id: eventData.event_id ?? generateUUID(),
      session_id: eventData.session_id ?? generateUUID(),
      timestamp: eventData.timestamp ?? new Date().toISOString(),
    };

    // Check if this event is a duplicate (all fields same except event_id)
    const isDuplicate = isEventDuplicate(completeEvent, events);

    // Only add the event if it's not a duplicate
    if (isDuplicate) {
      log('Skipping duplicate event in SSR:', completeEvent.event_type);
    } else {
      // Add new event to array
      events.push(completeEvent);
    }

    // If we have too many events, keep only the most recent ones to avoid cookie size limits
    // Browser cookies are typically limited to ~4KB
    const MAX_EVENTS = 10; // Adjust based on your event size

    if (events.length > MAX_EVENTS) {
      events = events.slice(-MAX_EVENTS);
    }

    // Serialize the events array to JSON
    const serializedEvents = JSON.stringify(events);

    // Check if serialized data is too large (approaching 4KB limit)
    if (encodeURIComponent(serializedEvents).length > 3500) {
      // If too large, only keep the current event
      events = [completeEvent];
      console.warn(
        'Event cookie size exceeded limit, only storing current event'
      );
    }

    const finalSerializedEvents = JSON.stringify(events);

    // Set the cookie directly on the response with consistent options
    res.setHeader('Set-Cookie', [
      `${EVENT_COOKIE_NAME}=${encodeURIComponent(finalSerializedEvents)}; Path=/; Max-Age=86400; SameSite=Strict${
        process.env.NEXT_PUBLIC_ENV === 'production' ? '; Secure' : ''
      }`,
    ]);
  } catch (error) {
    console.error('Failed to store event as cookie in SSR:', error);
  }
};

/**
 * Clears all stored events from the cookie.
 * Use this after events have been successfully submitted to the server.
 */
export const clearStoredEventsSSR = (
  res: NextApiResponse | GetServerSidePropsContext['res']
) => {
  try {
    // Set an empty array in the cookie to clear all events
    res.setHeader('Set-Cookie', [
      `${EVENT_COOKIE_NAME}=${encodeURIComponent(JSON.stringify([]))}; Path=/; Max-Age=86400; SameSite=Strict${
        process.env.NEXT_PUBLIC_ENV === 'production' ? '; Secure' : ''
      }`,
    ]);
    log('Cleared stored SSR events');
  } catch (error) {
    console.error('Failed to clear stored events in SSR:', error);
  }
};

/**
 * Initialize analytics event recovery system
 * Sets up automatic recovery of stored events on page load and network changes
 * @returns Cleanup function to remove event listeners
 */
export const initializeAnalyticsRecovery = (): (() => void) => {
  if (typeof window === 'undefined') {
    return () => {}; // No-op for SSR
  }

  // Recover events on initialization with a slight delay
  const initialRecoveryTimeout = setTimeout(() => {
    recoverStoredEvents();
  }, 2000); // Delay slightly to avoid interfering with initial page load

  // Handler for network coming back online
  const handleOnline = () => {
    log('Network connection restored, attempting to recover stored events');
    setTimeout(() => {
      recoverStoredEvents();
    }, 1000); // Small delay to ensure network is stable
  };

  // Handler for network going offline
  const handleOffline = () => {
    log('Network connection lost - critical events will be stored locally');
  };

  // Mobile browsers often skip `pagehide`, so flush on hidden too.
  const handleVisibilityChange = () => {
    if (document.visibilityState === 'hidden') {
      flushEventBatchOnHide();
    }
  };

  // Add event listeners
  window.addEventListener('online', handleOnline);
  window.addEventListener('offline', handleOffline);
  window.addEventListener('pagehide', flushEventBatchOnHide);
  document.addEventListener('visibilitychange', handleVisibilityChange);

  // Return cleanup function
  return () => {
    clearTimeout(initialRecoveryTimeout);
    window.removeEventListener('online', handleOnline);
    window.removeEventListener('offline', handleOffline);
    window.removeEventListener('pagehide', flushEventBatchOnHide);
    document.removeEventListener('visibilitychange', handleVisibilityChange);
  };
};
